/*
 * @Author: lyodon.li lyodon.li@feisu.com
 * @Date: 2025-04-22 19:57:27
 * @LastEditors: lyodon.li lyodon.li@feisu.com
 * @LastEditTime: 2026-01-04 16:33:13
 * @FilePath: /designcenter/src/api/modules/topologyGraph.ts
 * @Description: 拓扑图相关API接口
 */
import { getApiRequest } from '@/utils'
import type { IRes } from '@/types'

const request = getApiRequest()

// ==================== 类型定义 ====================

// 导出限制检查结果
export interface ExportLimitVO {
  /** 是否可以导出 */
  canExport: boolean
  /** 剩余等待时间（分钟） */
  remainingMinutes?: number
  /** 提示信息 */
  message?: string
}

// 线缆类型
export interface CableTypeVO {
  /** 线缆配置id */
  cableId: number
  /** 线缆类型 */
  cableTypeName: string
  /** 线缆产品id */
  cableProductId: number
  /** 模块产品id */
  moduleProductId: number
  /** 线缆颜色 */
  color: string
  /** 是否全局默认(0.否；1.是) */
  isDefault: number
}

// 线缆速率类型
export interface CableRateTypeVO {
  /** 线缆速率 */
  cableRateName: string
  /** 线缆类型集合 */
  cableTypeList: CableTypeVO[]
}

// 节点详情
export interface NodeDetailDTO {
  /** 节点名称 */
  nodeName: string
  /** 节点图片 */
  nodeImage: string
}

// 节点信息（请求和响应都使用这个结构）
export interface NodeInfoDTO {
  /** 源节点 */
  fromNode: NodeDetailDTO
  /** 目标节点 */
  targetNode: NodeDetailDTO
}

// 线缆模块详情请求参数
export interface CableModuleDetailDTO {
  /** 线缆产品id */
  cableProductId?: number
  /** 模块产品id */
  moduleProductId?: number
  /** 第二个模块产品id */
  module2ProductId?: number
  /** 节点列表 */
  nodeList?: NodeInfoDTO[]
}

// 线缆模块产品
export interface CableModuleProductVO {
  /** 产品id */
  productId: number
  /** 产品型号 */
  productModel: string
  /** 产品名称 */
  productName: string
  /** 产品图片 */
  productImage: string
  /** 产品标记（1.线缆；2.模块） */
  productMark: number
}

// 线缆模块详情响应
export interface CableModuleDetailVO {
  /** 线缆模块产品列表 */
  productList: CableModuleProductVO[]
  /** 节点列表 */
  nodeList: NodeInfoDTO[]
}

// 线缆模块搜索请求参数
export interface CableModuleSearchParams {
  /** 搜索类型(1-线缆;2-模块) */
  type: 1 | 2
  /** 关键字 */
  keyword: string
}

// 线缆模块搜索响应
export interface CableModuleProductSearchVO {
  /** 产品id */
  productId: number
  /** 产品图片 */
  productImage: string
  /** 产品名称 */
  productName: string
  /** 产品型号名称 */
  productModelName: string
  /** 产品价格 */
  productPrice: string
  /** 产品标签列表 */
  productLabelList: string[]
}

// 拓扑图查询参数
export interface QueryDiagramParams {
  projectId?: number
  templateId?: number
  [key: string]: unknown
}

// 保存拓扑图参数
export interface SaveTopologyGraphParams {
  projectId: number
  graphData: unknown
  [key: string]: unknown
}

// 创建项目参数
export interface CreateProjectByTemplateParams {
  projectId: number
  templateId: number
  [key: string]: unknown
}

// 产品列表查询参数
export interface ProductListParams {
  categoryId?: number
  keyword?: string
  pageNum?: number
  pageSize?: number
  [key: string]: unknown
}

// 导出限制检查参数
export interface CheckExportLimitParams {
  projectId: number
  exportType?: number
}

// 交换机产品匹配参数
export interface SwitchProductDTO {
  /** 起始产品ID */
  fromProductId: number
  /** 终止产品ID */
  targetProductId: number
}

// 匹配线缆结果
export interface MatchCableVO {
  /** 线缆速率 */
  cableRateName?: string
  /** 线缆配置id */
  cableId?: number
  /** 线缆类型 */
  cableTypeName?: string
  /** 线缆产品id */
  cableProductId?: number
  /** 模块产品id */
  moduleProductId?: number
  /** 线缆颜色 */
  color?: string
  /** 是否全局默认(0.否；1.是) */
  isDefault?: number
  /** 是否展示提示语(0.否；1.是) */
  isShow?: number
}

// ==================== API 函数 ====================

// 获取拓扑图数据
export async function getTopologyGraphData(data: QueryDiagramParams) {
  return request.post<IRes<unknown>>('/design/topology/diagram', data)
}

// 获取产品详情
export async function getProductDetail(id: string | number) {
  return request.get<IRes<unknown>>(`/design/product/parameter/${id}`)
}

// 保存拓扑图
export async function saveTopologyGraph(data: SaveTopologyGraphParams) {
  return request.post<IRes<unknown>>('/design/topology/saveProjectDiag', data)
}

// 切换模板创建项目
export async function createProjectByTemplate(data: CreateProjectByTemplateParams) {
  return request.post<IRes<unknown>>('/design/topology/createNewDiag', data)
}

// 获取线缆列表（查询线缆速率和类型）
export async function getCableList() {
  return request.post<IRes<CableRateTypeVO[]>>('/design/cable/list')
}

// 获取分支线缆列表
export async function getBranchCableList() {
  return request.get<IRes<CableRateTypeVO[]>>('/design/cable/branch/list')
}

// 查询线缆模块详情
export async function getCableModuleDetail(data: CableModuleDetailDTO[]) {
  return request.post<IRes<CableModuleDetailVO[]>>('/design/cable-module/detail', data)
}

// 线缆模块模糊搜索产品
export async function searchCableModuleProducts(params: CableModuleSearchParams) {
  return request.get<IRes<CableModuleProductSearchVO[]>>('/design/cable-module/search', { params })
}

// 创建分享副本
export async function createShareCopy(data: QueryDiagramParams) {
  return request.post<IRes<unknown>>('/design/topology/copyDiagram', data)
}

// 修改组件
export async function updateComponent(data: Record<string, unknown>) {
  return request.post<IRes<unknown>>('/design/component/update', data)
}

// 获取产品列表
export async function getProductList(data: ProductListParams) {
  return request.post<IRes<unknown>>('/design/product/list', data)
}

// 获取查看产品清单
export async function getTemplateChecklist(params: Record<string, unknown>) {
  return request.get<IRes<unknown>>(`/design/template/checklist`, { params })
}

// 切换模板覆盖当前项目
export async function coverTemplateProject(data: QueryDiagramParams) {
  return request.post<IRes<unknown>>('/design/topology/cover', data)
}

// 检查导出限制
export async function checkExportLimit(params: CheckExportLimitParams) {
  return request.post<IRes<ExportLimitVO>>('/design/export/checkExportLimit', null, { params })
}

// 交换机规则匹配线缆模块
export async function matchCableModule(data: SwitchProductDTO) {
  return request.post<IRes<MatchCableVO>>('/design/topology/match-cable', data)
}

// 校验是否首次使用批量生成线缆
export async function checkFirstCableLabel() {
  return request.get<IRes<unknown>>('/design/cable-label/check')
}

// 记录首次使用批量生成线缆
export async function recordFirstCableLabel() {
  return request.post<IRes<unknown>>('/design/cable-label/record')
}

// ==================== 失效线缆相关 ====================

// 失效线缆信息
export interface MissingCableVO {
  /** 线缆配置id */
  cableId: number
  /** 线缆速率 */
  cableRateName: string
  /** 线缆类型 */
  cableTypeName: string
}

// 查询失效线缆列表参数
export interface GetMissingCablesParams {
  /** 项目ID */
  projectId: number
}

// 替换线缆详情
export interface ReplaceCableDetailDTO {
  /** 原线缆配置id */
  cableId: number
  /** 目标线缆配置id */
  targetCableId: number
}

// 替换失效线缆参数
export interface ReplaceCableDTO {
  /** 项目/模板ID */
  id: number
  /** 替换线缆列表 */
  replaceList: ReplaceCableDetailDTO[]
}

// 查询拓扑图中失效线缆列表
export async function getMissingCables(params: GetMissingCablesParams) {
  return request.get<IRes<MissingCableVO[]>>('/design/topology/missing/cables', { params })
}

// 替换拓扑图中失效线缆
export async function replaceMissingCables(data: ReplaceCableDTO) {
  return request.post<IRes<boolean>>('/design/topology/missing/replace', data)
}
